import { useCallback, useEffect, useState, ReactNode } from 'react';
import { EntitlementsApiService, EntitlementsResponse } from '../services/api';
import { EntitlementsContext, type UseEntitlementsResult } from './useEntitlements.context';

// Type-only re-export — erased at compile time, so it does NOT break Fast
// Refresh (the `react-refresh/only-export-components` rule allows it).
// Runtime consumers (the `useEntitlements` hook itself) must import from
// `./useEntitlements.context` directly.
export type { UseEntitlementsResult } from './useEntitlements.context';

/** Default permissive features — used when the API fails or returns no data. */
const ALL_FEATURES_ENABLED: EntitlementsResponse['features'] = {
    maxAcknowledgmentsPerPeriod: null,
    maxActivePolicies: null,
    maxAdminUsers: null,
    maxTotalUsers: null,
    groupsEnabled: true,
    recurrenceEnabled: true,
    ssoEnabled: true,
    scheduledRemindersEnabled: true,
    analyticsEnabled: true,
    auditLogEnabled: true,
    dataRetentionDays: null,
    accountIsolation: false,
    adminDigestEnabled: true,
    templatesEnabled: true,
    versioningEnabled: true,
    scimEnabled: true,
};

/**
 * Provider that fetches the current tenant's entitlements exactly once per
 * authenticated session and shares the result with every consumer via context.
 * Mirrors `CurrentUserProvider`. Falls back to all features enabled on API
 * error (backward compat). Mount this alongside `CurrentUserProvider` so all
 * entitlement-gated pages share a single fetch instead of each calling the
 * endpoint independently.
 */
export function EntitlementsProvider({ children }: { children: ReactNode }) {
    const [loading, setLoading] = useState(true);
    const [tier, setTier] = useState<EntitlementsResponse['tier'] | null>(null);
    const [features, setFeatures] = useState<EntitlementsResponse['features'] | null>(null);
    const [currentPeriodAcknowledgments, setCurrentPeriodAcknowledgments] = useState(0);
    const [periodStartDate, setPeriodStartDate] = useState<string | null>(null);
    const [userPackQuantity, setUserPackQuantity] = useState<number | undefined>(undefined);
    // Default to false during load so the Manage Billing button stays hidden
    // until the API confirms a Stripe customer exists (#2070).
    const [hasBillingAccount, setHasBillingAccount] = useState<boolean>(false);

    /**
     * Single canonical fetch implementation, reused by the initial mount
     * effect and the exposed `refetch` callback. Stable across re-renders so
     * `refetch` is a stable reference and can be listed in effect deps without
     * re-running them.
     */
    const fetchEntitlements = useCallback(async (): Promise<void> => {
        try {
            const data = await EntitlementsApiService.getEntitlements();
            setTier(data.tier);
            setFeatures(data.features);
            /* Defensive: older entitlement records can omit the
               counter, in which case the API returns it as undefined.
               Coerce to 0 so the UI never renders an empty numerator
               in PlanUsagePanel's "Acknowledgments this period" row. (#1369) */
            setCurrentPeriodAcknowledgments(data.currentPeriodAcknowledgments ?? 0);
            setPeriodStartDate(data.periodStartDate ?? null);
            setUserPackQuantity(data.userPackQuantity);
            // Absent → false (hide Manage Billing button) per #2070.
            setHasBillingAccount(data.hasBillingAccount ?? false);
            setLoading(false);
        } catch {
            // Permissive fallback — don't block existing tenants if entitlements unavailable
            setTier('premium');
            setFeatures(ALL_FEATURES_ENABLED);
            setCurrentPeriodAcknowledgments(0);
            setPeriodStartDate(null);
            setUserPackQuantity(undefined);
            // True on error so a transient API failure doesn't hide the
            // Manage Billing button from a Standard-tier tenant who has a
            // real Stripe customer (#2070). Today's behavior: click → 404 → toast.
            setHasBillingAccount(true);
            setLoading(false);
        }
    }, []);

    // Single fetch on mount. `fetchEntitlements` is stable (empty useCallback
    // deps) so this effect runs once. The provider lives for the whole
    // authenticated session, so no in-flight cancellation guard is needed —
    // the canonical fetch reused by `refetch` stays dependency-free.
    useEffect(() => {
        void fetchEntitlements();
    }, [fetchEntitlements]);

    const value: UseEntitlementsResult = {
        loading,
        tier,
        features,
        currentPeriodAcknowledgments,
        periodStartDate,
        userPackQuantity,
        hasBillingAccount,
        refetch: fetchEntitlements,
    };

    return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}
